import gql from 'graphql-tag';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import apolloClient from '@/apollo/client';
import dayjs from '@/dayjs';
import { addLabelIndex } from '@/helpers/formatting';
import { DispatchTypeEnum } from '@/types';

const defaultImages = {
  BIRTHDAY:
    'https://res.cloudinary.com/salonized-eu-cld/image/upload/v1685021588/marketing_newsletter/Balloons_2_tfbvb3.png',
  MARKETING_CAMPAIGN:
    'http://res.cloudinary.com/salonized-eu-cld/image/upload/v1636022906/marketing_newsletter/os64rkcj7bsvzyf4555a.png',
  DEFAULT_MARKETING:
    'http://res.cloudinary.com/salonized-eu-cld/image/upload/v1636022906/marketing_newsletter/os64rkcj7bsvzyf4555a.png',
  REBOOK_REMINDER:
    'http://res.cloudinary.com/salonized-eu-cld/image/upload/v1636022906/marketing_newsletter/os64rkcj7bsvzyf4555a.png',
  WAITING_LIST:
    'http://res.cloudinary.com/salonized-eu-cld/image/upload/v1636022906/marketing_newsletter/os64rkcj7bsvzyf4555a.png',
  ONLINE_FORMS:
    'https://res.cloudinary.com/salonized-eu-cld/image/upload/v1684849136/marketing_newsletter/Email-cover-forms_ujdf60.png',
};

export const useMarketingStore = defineStore('marketing', () => {
  const resetState = ({
    setName,
    setId,
  }: {
    setName?: string;
    setId?: number | null;
  }) => {
    id.value = setId || null;
    name.value = setName || '';
    emailType.value = 'MARKETING_CAMPAIGN';
    hasLogo.value = true;
    hasImage.value = true;
    hasDiscount.value = false;
    hasButton.value = true;
    hasFooter.value = true;
    hasDeliverySchedule.value = false;
    deliverAt.value = dayjs().add(1, 'day').format();
    imageUrl.value = defaultImages.MARKETING_CAMPAIGN;
    subject.value = '';
    message.value = '';
    discount.value = null;
    discountMessage.value = '';
    buttonTitle.value = '';
    buttonUrl.value = '';
    color.value = '';
    allFilters.value = [];
    isSubmitted.value = false;
    messageIsInvalid.value = false;
    discountIsInvalid.value = false;
    buttonIsInvalid.value = false;
    scrollContainer.value = null;
    showFilters.value = false;
    rebookEnabled.value = false;
    emailTemplateDefault.value = null;
    dispatchType.value = DispatchTypeEnum.Manual;
  };

  const id = ref<number | null>(null);
  const emailType = ref<string>('MARKETING_CAMPAIGN');
  const hasLogo = ref<boolean>(true);
  const hasImage = ref<boolean>(true);
  const hasDiscount = ref<boolean>(false);
  const hasButton = ref<boolean>(true);
  const hasFooter = ref<boolean>(true);
  const hasDeliverySchedule = ref<boolean>(false);
  const discount = ref<any | null>(null);
  const discountMessage = ref<string>('');
  const discountCode = computed<string>(() => discount.value?.code || '');
  const discountCodeId = computed<number | null>(() =>
    discount.value?.id ? Number.parseInt(discount.value.id) : null,
  );
  const subject = ref<string>('');
  const message = ref<string>('');
  const defaultImageUrl = computed<string>(
    () => defaultImages[emailType.value as keyof typeof defaultImages],
  );
  const imageUrl = ref<string>(defaultImages.MARKETING_CAMPAIGN);
  const buttonTitle = ref<string>('');
  const buttonUrl = ref<string>('');
  const color = ref<string>('');
  const name = ref<string>('');
  const allFilters = ref<any[]>([]);
  const allFiltersForGraphql = computed(() => {
    return allFilters.value.map((filter) => {
      return {
        filterName: filter.filter_name.toUpperCase(),
        values: filter.values,
      };
    });
  });
  const isSubmitted = ref<boolean>(false);
  const messageIsInvalid = ref<boolean>(false);
  const discountIsInvalid = ref<boolean>(false);
  const buttonIsInvalid = ref<boolean>(false);
  const scrollContainer = ref<any | null>(null);
  const showFilters = ref<boolean>(false);
  const isInvalid = computed(() => {
    return (
      (hasDiscount.value && discountIsInvalid.value) ||
      messageIsInvalid.value ||
      (hasButton.value && buttonIsInvalid.value)
    );
  });
  const rebookEnabled = ref<boolean>(false);
  const deliverAt = ref<string>(dayjs().add(1, 'day').format());
  const dispatchType = ref<DispatchTypeEnum>(DispatchTypeEnum.Manual);
  const emailTemplateDefault = ref<any | null>(null);

  const setDiscountCode = (value: string) => {
    discount.value = value;
  };

  const setDiscountMessage = (value: string) => {
    discountMessage.value = value;
  };

  const resetContentOnly = () => {
    resetState({ setName: name.value, setId: id.value });
  };

  const resetToEmptyState = () => {
    resetState({ setName: '', setId: null });
  };

  const resetToDefaultState = () => {
    if (emailTemplateDefault.value) {
      setAttrs(emailTemplateDefault.value);
    } else if (id.value) {
      resetContentOnly();
    } else {
      resetToEmptyState();
    }
  };

  const clearFilters = () => {
    allFilters.value = [];
  };

  const setFilter = (value: any) => {
    value
      ? allFilters.value.push(addLabelIndex(allFilters.value, value))
      : (allFilters.value = []);
  };

  const setAllFilters = (value: any) => {
    allFilters.value = value.map((filter: any) =>
      addLabelIndex(allFilters.value, filter),
    );
  };

  const removeFilter = (value: any) => {
    allFilters.value = allFilters.value.filter(
      (filter) => filter.labelIndex !== value.labelIndex,
    );
  };

  const toggleFilters = () => {
    showFilters.value = !showFilters.value;
  };

  const setAttrs = (payload: any) => {
    const stateRefs: Record<string, { value: any }> = {
      id,
      emailType,
      hasLogo,
      hasImage,
      hasDiscount,
      hasButton,
      hasFooter,
      hasDeliverySchedule,
      discount,
      discountMessage,
      subject,
      message,
      imageUrl,
      buttonTitle,
      buttonUrl,
      color,
      name,
      allFilters,
      isSubmitted,
      messageIsInvalid,
      discountIsInvalid,
      buttonIsInvalid,
      scrollContainer,
      showFilters,
      rebookEnabled,
      deliverAt,
      dispatchType,
      emailTemplateDefault,
    };

    for (const [key, value] of Object.entries(payload)) {
      if (key === 'discountCode' && value) {
        discount.value = value;
      } else if (key === 'filters' && value) {
        allFilters.value = (value as any[]).map((filter) => {
          const filterData = addLabelIndex(allFilters.value, filter);
          filterData.filter_name = filterData.filterName;
          filterData.filterName = undefined;
          return filterData;
        });
      } else if (key in stateRefs) {
        stateRefs[key].value = value;
      }
    }
  };

  const formInput = computed(
    () =>
      (
        selectedEmailType: string,
        options: {
          newRecord?: boolean;
          sendEmail?: boolean;
          testEmail?: string;
        } = {},
      ) => {
        const { newRecord = true, sendEmail = false, testEmail } = options;

        const input: Record<string, any> = {
          name: name.value,
          hasLogo: hasLogo.value,
          hasImage: hasImage.value,
          hasDiscount: hasDiscount.value,
          hasButton: hasButton.value,
          hasFooter: hasFooter.value,
          hasDeliverySchedule: hasDeliverySchedule.value,
          subject: subject.value,
          message: message.value,
          color: color.value,
          id: id.value,
          dispatchType: dispatchType.value,
        };

        if (newRecord && !id.value) {
          input.id = undefined;
        } else {
          input.id = id.value;
        }

        if (testEmail) {
          input.testEmail = testEmail;
        }

        if (!sendEmail) {
          input.emailType = selectedEmailType;
        }

        if (selectedEmailType === 'MARKETING_CAMPAIGN') {
          input.filters = allFiltersForGraphql.value;
        }

        if (hasImage.value && imageUrl.value) {
          input.imageUrl = imageUrl.value;
        }

        if (hasDiscount.value && discountCodeId.value) {
          input.discountMessage = discountMessage.value;
          input.discountCodeId = discountCodeId.value;
        }

        if (hasButton.value) {
          input.buttonTitle = buttonTitle.value;
          input.buttonUrl = buttonUrl.value;
        }

        if (hasDeliverySchedule.value && deliverAt.value) {
          input.deliverAt = deliverAt.value;
        }

        if (selectedEmailType !== 'WAITING_LIST') {
          input.dispatchType = null;
        }

        return input;
      },
  );

  const createEmailTemplate = () => {
    return new Promise((resolve) => {
      apolloClient
        .mutate({
          mutation: gql`
          mutation createEmailTemplate($input: CreateEmailTemplateInput!) {
            createEmailTemplate(input: $input) {
              emailTemplate {
                id
              }
            }
          }
        `,
          variables: {
            input: formInput.value('MARKETING_CAMPAIGN', {
              newRecord: true,
            }),
          },
        })
        .then(() => {
          resolve(null);
        });
    });
  };

  const updateEmailTemplate = (emailType: string) => {
    return new Promise((resolve) => {
      apolloClient
        .mutate({
          mutation: gql`
          mutation updateEmailTemplate($input: UpdateEmailTemplateInput!) {
            updateEmailTemplate(input: $input) {
              emailTemplate {
                emailType
              }
            }
          }
        `,
          variables: {
            input: formInput.value(emailType, {
              newRecord: false,
            }),
          },
        })
        .then(() => {
          resolve(null);
        });
    });
  };

  const sendMarketingEmail = (options?: { testEmail?: string }) => {
    const { testEmail } = options || {};

    return new Promise((resolve) => {
      apolloClient
        .mutate({
          mutation: gql`
          mutation sendMarketingEmail($input: SendMarketingEmailInput!) {
            sendMarketingEmail(input: $input) {
              errors
            }
          }
        `,
          variables: {
            input: formInput.value('MARKETING_CAMPAIGN', {
              newRecord: true,
              testEmail,
              sendEmail: true,
            }),
          },
        })
        .then(() => {
          resolve(null);
        });
    });
  };

  return {
    id,
    emailType,
    hasLogo,
    hasImage,
    hasDiscount,
    hasButton,
    hasFooter,
    hasDeliverySchedule,
    deliverAt,
    defaultImageUrl,
    imageUrl,
    subject,
    message,
    discount,
    discountMessage,
    discountCode,
    discountCodeId,
    buttonTitle,
    buttonUrl,
    color,
    allFilters,
    allFiltersForGraphql,
    isSubmitted,
    messageIsInvalid,
    discountIsInvalid,
    buttonIsInvalid,
    scrollContainer,
    showFilters,
    isInvalid,
    rebookEnabled,
    name,
    emailTemplateDefault,
    dispatchType,
    formInput,
    setDiscountCode,
    setDiscountMessage,
    resetContentOnly,
    resetToEmptyState,
    resetToDefaultState,
    clearFilters,
    setFilter,
    setAllFilters,
    removeFilter,
    toggleFilters,
    setAttrs,
    createEmailTemplate,
    updateEmailTemplate,
    sendMarketingEmail,
  };
});
